<template>
  <BModal
    :model-value="show"
    title="収集カレンダー基本曜日設定"
    size="lg"
    ok-title="OK"
    cancel-title="キャンセル"
    @ok="handleSave"
    @hide="$emit('close')"
  >
    <!-- 収集地区・ごみ種別 -->
    <div class="mb-3">
      <p><strong>収集地区:</strong> {{ districtName }}</p>
      <p><strong>ごみ種別:</strong> {{ wasteTypeName }}</p>
    </div>

    <div class="border rounded p-3 mb-3">
      <!-- 毎週設定 -->
      <div class="form-check mb-2">
        <input class="form-check-input" type="radio" name="patternType" id="typeWeekly" value="weekly" v-model="patternType">
        <label class="form-check-label fw-bold" for="typeWeekly">毎週</label>
      </div>
      <div class="ms-4 mb-4" :class="{ 'opacity-50': patternType !== 'weekly' }">
        <div class="d-flex flex-wrap gap-2">
          <div v-for="(day, index) in days" :key="index" class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`weekly-${index}`"
              :value="index"
              v-model="weeklySettings.daysOfWeek"
              :disabled="patternType !== 'weekly'"
            >
            <label class="form-check-label" :for="`weekly-${index}`">{{ day }}</label>
          </div>
        </div>
      </div>

      <!-- 第X週設定 -->
      <div class="form-check mb-2">
        <input class="form-check-input" type="radio" name="patternType" id="typeSpecific" value="specific-week" v-model="patternType">
        <label class="form-check-label fw-bold" for="typeSpecific">第X週目</label>
      </div>
      <div class="ms-4 mb-4" :class="{ 'opacity-50': patternType !== 'specific-week' }">
        <table class="table table-bordered table-sm text-center bg-white">
          <thead>
            <tr>
              <th></th>
              <th v-for="d in days" :key="d">{{ d }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="week in [1, 2, 3, 4, 5]" :key="week">
              <th>第{{ week }}</th>
              <td v-for="(day, dIndex) in days" :key="dIndex">
                <input
                  type="checkbox"
                  class="form-check-input"
                  :checked="isSpecificWeekChecked(dIndex, week)"
                  @change="toggleSpecificWeek(dIndex, week)"
                  :disabled="patternType !== 'specific-week'"
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 隔週設定 -->
      <div class="form-check mb-2">
        <input class="form-check-input" type="radio" name="patternType" id="typeBiweekly" value="biweekly" v-model="patternType">
        <label class="form-check-label fw-bold" for="typeBiweekly">隔週</label>
      </div>
      <div class="ms-4" :class="{ 'opacity-50': patternType !== 'biweekly' }">
        <div class="d-flex gap-4 mb-2">
          <div class="form-check">
            <input class="form-check-input" type="radio" name="biweeklyStart" id="startFirst" value="first-week" v-model="biweeklySettings.startPattern" :disabled="patternType !== 'biweekly'">
            <label class="form-check-label" for="startFirst">第1週開始</label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="radio" name="biweeklyStart" id="startSecond" value="second-week" v-model="biweeklySettings.startPattern" :disabled="patternType !== 'biweekly'">
            <label class="form-check-label" for="startSecond">第2週開始</label>
          </div>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <div v-for="(day, index) in days" :key="index" class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`biweekly-${index}`"
              :value="index"
              v-model="biweeklySettings.daysOfWeek"
              :disabled="patternType !== 'biweekly'"
            >
            <label class="form-check-label" :for="`biweekly-${index}`">{{ day }}</label>
          </div>
        </div>
      </div>
    </div>
  </BModal>
</template>

<script setup>
import { ref, watch } from 'vue'
import { BModal } from 'bootstrap-vue-next'

const props = defineProps({
  show: Boolean,
  districtName: String,
  wasteTypeName: String,
  initialPattern: Object
})

const emit = defineEmits(['close', 'save'])

const days = ['日', '月', '火', '水', '木', '金', '土']
const patternType = ref('weekly')

const weeklySettings = ref({
  daysOfWeek: []
})

const specificWeekSettings = ref({
  patterns: []
})

const biweeklySettings = ref({
  startPattern: 'first-week',
  daysOfWeek: []
})

watch(() => props.show, (newVal) => {
  if (newVal) {
    initializeData()
  }
})

const initializeData = () => {
  patternType.value = 'weekly'
  weeklySettings.value.daysOfWeek = []
  specificWeekSettings.value.patterns = []
  biweeklySettings.value = { startPattern: 'first-week', daysOfWeek: [] }

  if (props.initialPattern) {
    patternType.value = props.initialPattern.patternType
    const settings = props.initialPattern.settings

    if (patternType.value === 'weekly') {
      weeklySettings.value.daysOfWeek = [...settings.daysOfWeek]
    } else if (patternType.value === 'specific-week') {
      specificWeekSettings.value.patterns = JSON.parse(JSON.stringify(settings.patterns))
    } else if (patternType.value === 'biweekly') {
      biweeklySettings.value = JSON.parse(JSON.stringify(settings))
    }
  }
}

const isSpecificWeekChecked = (dayIndex, week) => {
  const pattern = specificWeekSettings.value.patterns.find(p => p.dayOfWeek === dayIndex)
  return pattern && pattern.weeks.includes(week)
}

const toggleSpecificWeek = (dayIndex, week) => {
  if (patternType.value !== 'specific-week') return

  let patternIndex = specificWeekSettings.value.patterns.findIndex(p => p.dayOfWeek === dayIndex)

  if (patternIndex === -1) {
    specificWeekSettings.value.patterns.push({
      dayOfWeek: dayIndex,
      weeks: [week]
    })
  } else {
    const pattern = specificWeekSettings.value.patterns[patternIndex]
    const weekIndex = pattern.weeks.indexOf(week)

    if (weekIndex === -1) {
      pattern.weeks.push(week)
    } else {
      pattern.weeks.splice(weekIndex, 1)
      if (pattern.weeks.length === 0) {
        specificWeekSettings.value.patterns.splice(patternIndex, 1)
      }
    }
  }
}

const handleSave = () => {
  let settings = null

  if (patternType.value === 'weekly') {
    settings = { daysOfWeek: weeklySettings.value.daysOfWeek }
  } else if (patternType.value === 'specific-week') {
    settings = {
      patterns: specificWeekSettings.value.patterns.map(p => ({
        dayOfWeek: p.dayOfWeek,
        weeks: p.weeks.sort((a, b) => a - b)
      })).sort((a, b) => a.dayOfWeek - b.dayOfWeek)
    }
  } else if (patternType.value === 'biweekly') {
    settings = { ...biweeklySettings.value }
  }

  emit('save', {
    patternType: patternType.value,
    settings: settings
  })
}
</script>

<style scoped>
.opacity-50 {
  opacity: 0.5;
  pointer-events: none;
}
</style>
